import type { ReactNode } from 'react';
import { Alert, Box, Chip, Grid, Link, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { formatPhone } from '@/features/customers/customerForm';
import { customerStatusLabels, formatHistoryTimestamp } from '@/features/operations/labels';
import { usePriceDisplay } from '@/features/operations/PriceDisplayProvider';
import type { ActiveReservation } from '@/features/operations/types';

const DAY_MS = 24 * 60 * 60 * 1000;

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Grid item xs={12} sm={6} md={4}>
      <Typography variant="caption" color="text.secondary" component="p">
        {label}
      </Typography>
      <Typography variant="body2" component="div">
        {children}
      </Typography>
    </Grid>
  );
}

/// "Quedan 2 días", "Vence hoy": how much is left, counted in whole days from now.
function remainingLabel(expiresAt: Date, now: Date): string {
  const days = Math.ceil((expiresAt.getTime() - now.getTime()) / DAY_MS);
  if (days <= 1) return 'Vence en menos de un día';
  return `Quedan ${days} días`;
}

/**
 * The active reservation of a device, on its sheet (VEN-2): who it is for, until when, at what
 * price and who made it.
 *
 * VEN-5's sweep releases a reservation that ran out every ten minutes, so one whose end just
 * passed can still be on the device for a while (or longer, if the worker is down). The block
 * says so, so nobody takes it for a live one.
 */
export function ActiveReservationBlock({
  reservation,
  now = new Date(),
}: {
  reservation: ActiveReservation;
  now?: Date;
}) {
  const { formatNullablePrice } = usePriceDisplay();
  const expiresAt = new Date(reservation.expiresAt);
  const expired = expiresAt.getTime() <= now.getTime();

  return (
    <Box
      component="section"
      aria-label="Reserva activa"
      sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2 }}
    >
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography variant="subtitle1" component="h3">
            Reserva activa
          </Typography>
          <Chip size="small" label={`#${reservation.reference}`} />
        </Stack>
        {expired ? (
          <Alert severity="warning">
            Venció el {formatHistoryTimestamp(reservation.expiresAt)}. El equipo se libera
            automáticamente en unos minutos.
          </Alert>
        ) : null}
        <Grid container spacing={2}>
          <Field label="Comprador">
            {reservation.buyer.name}
            {reservation.account ? (
              <>
                {' '}
                <Link
                  component={RouterLink}
                  to={`/admin/customers/${reservation.account.id}`}
                  underline="hover"
                >
                  (cuenta {customerStatusLabels[reservation.account.status]?.toLowerCase()})
                </Link>
              </>
            ) : null}
          </Field>
          <Field label="Mail">{reservation.buyer.email}</Field>
          <Field label="Teléfono">{formatPhone(reservation.buyer.phone)}</Field>
          <Field label="Vence">
            {formatHistoryTimestamp(reservation.expiresAt)}
            {expired ? null : (
              <Typography variant="caption" color="text.secondary" component="p">
                {remainingLabel(expiresAt, now)}
              </Typography>
            )}
          </Field>
          <Field label="Precio reservado">{formatNullablePrice(reservation.priceMinor)}</Field>
          <Field label="Reservado por">
            {reservation.createdBy ? reservation.createdBy.fullName : 'El comprador, desde la web'}
            <Typography variant="caption" color="text.secondary" component="p">
              el {formatHistoryTimestamp(reservation.createdAt)}
            </Typography>
          </Field>
        </Grid>
      </Stack>
    </Box>
  );
}
